import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Star, Tv, Play, Trash2, RefreshCw } from 'lucide-react';
import { streamFlowApi } from '@lib/streamFlowApi';
import type { FamilyMember, FavoriteEntry } from '@lib/streamFlowApi';

/**
 * Favorites — per-member starred-channels listing.
 *
 * Reads sf_favorites directly via Supabase (member-scoped RLS). Same shape as
 * WatchHistory: pick a member, render the list with Watch / Remove actions.
 * The favorite-toggle on the channel grid writes to the same table; this page
 * is the read view.
 */
export default function Favorites() {
  const [members, setMembers] = useState<FamilyMember[]>([]);
  const [memberId, setMemberId] = useState<string | null>(null);
  const [entries, setEntries] = useState<FavoriteEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    streamFlowApi.listMembers().then((res) => {
      if (res.success && res.data) {
        setMembers(res.data);
        if (res.data.length > 0) setMemberId(res.data[0].id);
      } else {
        setError(res.error?.message ?? 'Failed to load family members');
      }
    });
  }, []);

  const refresh = useCallback(async () => {
    if (!memberId) return;
    setLoading(true);
    setError(null);
    const res = await streamFlowApi.listFavorites(memberId);
    if (res.success && res.data) {
      setEntries(res.data);
    } else {
      setError(res.error?.message ?? 'Failed to load favorites');
    }
    setLoading(false);
  }, [memberId]);

  useEffect(() => { refresh(); }, [refresh]);

  const remove = async (channelId: string) => {
    if (!memberId) return;
    // Optimistic — match the channel-grid star toggle behavior.
    setEntries((prev) => prev.filter((e) => e.channel_id !== channelId));
    const res = await streamFlowApi.removeFavorite(memberId, channelId);
    if (!res.success) refresh();
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      <Link to="/" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-900 mb-3">
        <ArrowLeft className="w-4 h-4" /> Back to dashboard
      </Link>

      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Star className="w-6 h-6" /> Favorites</h1>
          <p className="text-sm text-gray-500 mt-1">Starred channels for {members.find(m => m.id === memberId)?.display_name ?? 'this member'}.</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={memberId ?? ''}
            onChange={(e) => setMemberId(e.target.value || null)}
            className="px-3 py-2 border rounded-lg text-sm bg-white"
          >
            {members.map((m) => (
              <option key={m.id} value={m.id}>{m.display_name}</option>
            ))}
          </select>
          <button onClick={refresh} className="p-2 border rounded-lg text-gray-500 hover:bg-gray-50" aria-label="Refresh">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {error && <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>}

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading…</div>
      ) : entries.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          No favorites yet. Tap the star on any channel to save it here.
        </div>
      ) : (
        <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
          {entries.map((fav) => (
            <li key={fav.id} className="border rounded-lg p-3 bg-white hover:shadow-md transition-all">
              <div className="w-full aspect-video bg-gray-100 rounded mb-2 flex items-center justify-center overflow-hidden">
                {fav.channel?.logo_url ? (
                  <img src={fav.channel.logo_url} alt="" className="w-full h-full object-contain" />
                ) : (
                  <Tv className="w-8 h-8 text-gray-300" />
                )}
              </div>
              <p className="text-sm font-medium truncate">{fav.channel?.name ?? 'Unknown channel'}</p>
              {fav.channel?.category && (
                <p className="text-xs text-gray-400 truncate">{fav.channel.category}</p>
              )}
              <div className="flex items-center gap-2 mt-2">
                <Link
                  to={`/watch/${fav.channel_id}`}
                  className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-1.5 text-xs font-medium bg-slate-900 text-white rounded-lg hover:bg-slate-700"
                >
                  <Play className="w-3 h-3" /> Watch
                </Link>
                <button
                  onClick={() => remove(fav.channel_id)}
                  className="p-1.5 text-gray-400 hover:text-red-500 border rounded-lg"
                  aria-label="Remove favorite"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
